import { useMedplum } from '@medplum/react-hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFhirRealtimeOptional } from '../../hooks/fhirRealtimeContext';
import { isMockMedplumMode } from '../../lib/isMockMedplumMode';
import { useEffectivePortalPatientId } from '../../hooks/usePortalSubjectPatientId';
import type { AppointmentsPageData } from './data/getAppointmentsPageData';
import { usePortalAppointmentFlowMessageRefresh } from '../../pages/client-portal/hooks/usePortalAppointmentFlowMessageRefresh';
import { getAppointmentsPageData } from './data/getAppointmentsPageData';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';
import { createLatestRequestGuard, startVisiblePoll } from './latestRequest';

/**
 * Hook that loads appointments data using the live/mock adapter.
 * Uses live Medplum data when {@link isMockMedplumMode} is false (staging/prod are never mock).
 * In live mode errors surface as empty state — no silent mock fallback.
 */
export function useAppointmentsData(): {
  data: AppointmentsPageData | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const medplum = useMedplum();
  const portalPatientId = useEffectivePortalPatientId();
  const latestRequestRef = useRef(createLatestRequestGuard());
  const [data, setData] = useState<AppointmentsPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const hasDataRef = useRef(false);

  // Dev defaults to mock Medplum for staff demos, but a signed-in client must never see
  // the static demo rows (Medication management, Telehealth follow-up, etc.) on the portal.
  const useLive = !isMockMedplumMode() || Boolean(portalPatientId);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  const loadAppointments = useCallback(
    async (opts?: { showLoading?: boolean }) => {
      const requestId = latestRequestRef.current.next();
      const showLoading = opts?.showLoading ?? !hasDataRef.current;
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      try {
        if (useLive) {
          medplum.invalidateSearches('Appointment');
        }
        const result = await getAppointmentsPageData({ live: useLive, medplum, patientId: portalPatientId });
        // Only the latest load writes: a slower earlier load must not overwrite a newer one.
        if (!latestRequestRef.current.isLatest(requestId)) return;
        setData(result);
        hasDataRef.current = true;
      } catch (err) {
        if (latestRequestRef.current.isLatest(requestId)) setError(getUserFacingErrorMessage(err, 'Failed to load appointments'));
      } finally {
        // The latest load clears `loading` (also one that began as a silent refresh while an earlier load was pending).
        if (latestRequestRef.current.isLatest(requestId)) {
          setLoading(false);
        }
      }
    },
    [medplum, useLive, portalPatientId],
  );

  const loadAppointmentsRef = useRef(loadAppointments);
  loadAppointmentsRef.current = loadAppointments;

  useEffect(() => {
    hasDataRef.current = false;
    setData(null);
    setLoading(true);
  }, [portalPatientId]);

  useEffect(() => {
    void loadAppointments({ showLoading: true });
  }, [loadAppointments, tick]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const fhirRealtime = useFhirRealtimeOptional();

  useEffect(() => {
    if (!useLive || !fhirRealtime) return;
    return fhirRealtime.onClientPortalRefresh(() => {
      void loadAppointmentsRef.current({ showLoading: false });
    });
  }, [fhirRealtime, useLive]);

  // Client policies cannot subscribe to Appointment (websocket notifications skip hiddenFields), so
  // staff-side changes reach the portal by a silent poll while the page is visible.
  useEffect(() => {
    if (!useLive) return undefined;
    return startVisiblePoll(() => void loadAppointmentsRef.current({ showLoading: false }));
  }, [useLive]);

  usePortalAppointmentFlowMessageRefresh(refresh);

  return { data, loading, error, refresh };
}
